import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const payloadKey = {
  "vehicle-make": "name",
  "vehicle-model": "name",
  "vehicle-year": "name",
  "vehicle-color": "name",
  "vehicle-type": "name",
  country: "name",
  state: "name",
};

function Model({
  show,
  onHide,
  initialState,
  editData,
  title,
  submit,
  EndPoint,
  loading,
  makes = null,
  countries = null,
  onSuccess,
}) {
  const { t } = useTranslation();
  const key = payloadKey[EndPoint];
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({
      ...prev,
      [name]: value.trim()
        ? ""
        : `${t("this")} ${t("field")} ${t("is")} ${t("required")}`,
    }));
  };

  // Validate the form data before submitting
  const validateForm = () => {
    const newErrors = {};
    const value = formData[key];

    // Validate if vehicle-year length is not greater than 4 characters
    if (EndPoint === "vehicle-year" && value.length > 4) {
      newErrors[key] = `${t("vehicle")} ${t("year")} ${t("must")} ${t(
        "not",
      )} ${t("be")} ${t("more")} ${t("than")} 4 ${t("characters")}.`;
    } else if (!value || value.trim() === "") {
      newErrors[key] = `${t("this")} ${t("field")} ${t("is")} ${t("required")}`;
    }

    if (EndPoint === "vehicle-model" && !formData.make_id) {
      newErrors.make_id = `${t("this")} ${t("field")} ${t("is")} ${t(
        "required",
      )}`;
    }

    if (EndPoint === "state" && !formData.country_id) {
      newErrors.country_id = `${t("this")} ${t("field")} ${t("is")} ${t(
        "required",
      )}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit for both create and update
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    toast.dismiss();

    try {
      const response = await submit({
        data: formData,
        endPoint: EndPoint,
      }).unwrap();

      if (response.statusCode === 200 || response.statusCode === 201) {
        handleClose();
        onSuccess();
      }
    } catch (err) {
      toast.error(err?.data?.message || "Something went wrong");
    }
  };

  // Initialize form data when editData or initialState is updated
  useEffect(() => {
    if (editData) {
      setFormData(editData);
    } else {
      setFormData(initialState);
    }
  }, [initialState, editData]);

  const handleClose = () => {
    setFormData(initialState);
    setErrors({});
    onHide();
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered className="VehicleModal">
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit}>
            <ul className="form d-flex flex-wrap p-0 pt-2">
              {makes && (
                <li className="col-12">
                  <select
                    className="formSelect"
                    name="make_id"
                    value={formData.make_id}
                    onChange={handleChange}
                  >
                    <option value="">
                      {t("select")} {t("make")}
                    </option>
                    {makes?.length > 0
                      ? makes?.map((item, i) => (
                          <option value={item.id} key={i}>
                            {item.name}
                          </option>
                        ))
                      : ""}
                  </select>
                  {errors?.make && (
                    <span style={{ color: "red" }}>{errors.make}</span>
                  )}
                </li>
              )}
              {countries && (
                <li className="col-12">
                  <select
                    className="formSelect"
                    name="country_id"
                    value={formData.country_id}
                    onChange={handleChange}
                  >
                    <option value="">
                      {t("select")} {t("country")}
                    </option>
                    {countries?.length > 0
                      ? countries?.map((item, i) => (
                          <option value={item.id} key={i}>
                            {item.name}
                          </option>
                        ))
                      : ""}
                  </select>
                  {errors?.country && (
                    <span style={{ color: "red" }}>{errors.country}</span>
                  )}
                </li>
              )}
              <li className="col-12">
                <input
                  type={EndPoint === "vehicle-year" ? "number" : "text"}
                  className="formInput custom-field-one me-2"
                  placeholder={`${t("enter")} ${key}`}
                  name={key}
                  value={formData[key] || ""}
                  onChange={handleChange}
                />
                {errors[key] && (
                  <div className="text-danger mb-2">{errors[key]}</div>
                )}
              </li>
              {EndPoint === "country" && (
                <li className="col-12">
                  <input
                    type="text"
                    className="formInput custom-field-one me-2"
                    placeholder={`${t("enter")} ${t("country")} ${t("code")}`}
                    name="code"
                    value={formData.code || ""}
                    onChange={handleChange}
                  />
                </li>
              )}

              <li className="col-12 text-end">
                <Button disabled={loading} type="submit">
                  {loading ? (
                    <div className="spinner-border loaderStyle" role="status">
                      <span className="sr-only p-0"></span>
                    </div>
                  ) : (
                    t("btns.submit")
                  )}
                </Button>
              </li>
            </ul>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default Model;
